/** One-click shortcuts to each window where runs overlap (#946). */
import React from 'react';
import { formatClock, formatDayLabel } from './scheduleTime';

const MIN = 60_000;

export interface ContentionChipsProps {
  /** [startMinute, endMinute) indices relative to windowStart, with peak depth. */
  ranges: Array<{ from: number; to: number; peak: number }>;
  windowStart: number;
  displayTimezone: string;
  /** Prefix each chip with a weekday when the horizon spans more than a day. */
  multiDay: boolean;
  zoomed: boolean;
  onZoom: (start: number, end: number) => void;
  onReset: () => void;
}

export const ContentionChips: React.FC<ContentionChipsProps> = ({
  ranges,
  windowStart,
  displayTimezone,
  multiDay,
  zoomed,
  onZoom,
  onReset,
}) => (
  <div className="flex flex-wrap items-center gap-2 py-3">
    <span className="text-xs text-muted-foreground">
      {ranges.length ? 'Contention:' : 'No overlapping runs in this horizon.'}
    </span>
    {ranges.map(({ from, to, peak }) => {
      const start = windowStart + from * MIN;
      const end = windowStart + to * MIN;
      // Over a multi-day horizon the same daily cluster repeats — name the day
      // so several identical "3:00 AM" chips are tellable apart.
      const day = multiDay ? `${formatDayLabel(start, displayTimezone)} ` : '';
      return (
        <button
          key={from}
          type="button"
          onClick={() => onZoom(start, end)}
          className="rounded-full border border-warning/35 bg-warning/10 px-2.5 py-1 text-xs font-medium tabular-nums text-warning-foreground hover:bg-warning/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {day}
          {formatClock(start, displayTimezone)}–{formatClock(end, displayTimezone)} · {peak} at once
        </button>
      );
    })}
    {zoomed && (
      <button
        type="button"
        onClick={onReset}
        className="rounded-full border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        Show whole horizon
      </button>
    )}
  </div>
);
